import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import "./App.css";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { Amplify, Auth } from "aws-amplify";
import * as Sentry from "@sentry/browser";
import * as SentryReact from "@sentry/react";
import awsConfig from "./config/aws.exports";
import { webCliendId } from "./config/config";

const root = ReactDOM.createRoot(document.getElementById("root"));

// Single Amplify + Auth config at startup (includes OAuth) so callbacks and sign-out work reliably
// IMPORTANT: Auth is configured via Auth.configure() with full OAuth settings from awsConfig.
// Do NOT add a separate Auth block to Amplify.configure() as it would override OAuth settings.
Auth.configure({
  ...awsConfig,
  aws_user_pools_web_client_id: webCliendId,
  mandatorySignIn: false,
});

Amplify.configure({
  // Auth is already configured above via Auth.configure() with OAuth settings.
  // Adding Auth here would override those settings and break federated sign-out.
  API: {
    graphql_headers: async () => {
      const session = await Auth.currentSession();
      return {
        Authorization: session.getIdToken().getJwtToken(),
      };
    },
  },
});

const myAppConfig = {
  aws_appsync_graphqlEndpoint: import.meta.env.VITE_AWS_APPSYNC_GRAPHQLENDPOINT ? import.meta.env.VITE_AWS_APPSYNC_GRAPHQLENDPOINT : "https://dev-sponsor-api.konfhub.com/graphql",
  aws_appsync_region: import.meta.env.VITE_AWS_COGNITO_REGION ? import.meta.env.VITE_AWS_COGNITO_REGION : "ap-southeast-1",
  aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
};
Amplify.configure(myAppConfig);

SentryReact.init({
  dsn: "https://6fa34e10ba1c4e21c88d717f2b06be33@o4508326426181632.ingest.de.sentry.io/4508496845930576",
  integrations: [
    SentryReact.browserTracingIntegration(),
    SentryReact.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.VITE_ENVIRONMENT ?? 'local',
  ignoreErrors: [
			/chrome-extension:/,
			/ExtensionsBundleLoader/,
			/extensions::/,
			/Network Error/,
			/ETIMEDOUT/,
			/ECONNABORTED/,
			/ AxiosError /,
			/fetch failed/,
			/node_modules/,
			"No current user" // ignore expected AWS Amplify session expiry error to reduce Sentry noise
		],
  beforeSend(event) {
			// Block localhost logs
			if (typeof window !== 'undefined' && window.location?.hostname === "localhost") return null;
      		if (isNodeModulesError(event)) return null;

			// Block anything involving /monitoring
			const url = event.request?.url || event.transaction || "";
			if (typeof url === "string" && url.includes("/monitoring")) return null;

			if (isBrowserExtensionError(event)) return null;

			return event;
		},
});

function isNodeModulesError(event) {
	if (!event.exception?.values) return false;

	for (const exception of event.exception.values) {
		const frames = exception.stacktrace?.frames || [];
		const hasNodeModules = frames.some(frame => {
			const filename = frame.filename || "";
			const absPath = frame.abs_path || "";
			return (
				filename.includes("node_modules") ||
				absPath.includes("node_modules") ||
				/webpack:\/\/\/\.\/node_modules\//.test(absPath) ||
				absPath.includes("webpack:///./node_modules/") ||
				absPath.includes("~")
			);
		});
		if (hasNodeModules) return true;
	}

	return false;
}

function isBrowserExtensionError(event) {
	const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
	return frames.some(
		f => f.filename?.startsWith("chrome-extension:") || f.filename?.startsWith("moz-extension:")
	);
}

root.render(
  // <React.StrictMode>
  <BrowserRouter>
    <SentryReact.ErrorBoundary>
      <App />
    </SentryReact.ErrorBoundary>
  </BrowserRouter>
  // </React.StrictMode>,
);

const env = import.meta.env.VITE_ENVIRONMENT ?? "local";

if (env === "prod") {
	(function(c,l,a,r,i,t,y){
          c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
          t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
          y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "tq3jg6l6wr");
}
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
